import { OwnerDetails } from '@/app/(protected)/boards/share/shareTypes';
import { create } from 'zustand';
import type { Version } from '@/app/(protected)/boards/[boardId]/versionControl/versionTypes';
import { FitView, Viewport } from '@xyflow/react';

type BoardStoreState = {
  ownerDetails: OwnerDetails | null;
  setOwnerDetails: (ownerDetails: OwnerDetails | null) => void;
  isPreviewMode: boolean;
  setIsPreviewMode: (isPreviewMode: boolean) => void;
  previewVersion: Version | null;
  setPreviewVersion: (version: Version | null) => void;
  startPreview: (
    version: Version,
    screenViewBeforePreview: { x: number; y: number; zoom: number }
  ) => void;
  endPreview: () => void;
  isRestoringVersion: boolean;
  setIsRestoringVersion: (isRestoringVersion: boolean) => void;
  notEditable: boolean;
  setNotEditable: (notEditable: boolean) => void;
  versionUpdated: Version | null;
  setVersionUpdated: (versionUpdated: Version | null) => void;
  screenViewBeforePreview: { x: number; y: number; zoom: number } | null;
  setScreenViewBeforePreview: (
    screenViewBeforePreview: { x: number; y: number; zoom: number } | null
  ) => void;
  isGlobalPasting: boolean;
  setGlobalPasting: (isPasting: boolean) => void;
  fullscreenNodeId: string | null;
  setFullscreenNodeId: (nodeId: string | null) => void;
  showVideoFileLinkDialog: boolean;
  setShowVideoFileLinkDialog: (show: boolean) => void;
  nodeDragMode: 'moveRight' | 'moveTogether' | null;
  setNodeDragMode: (mode: 'moveRight' | 'moveTogether' | null) => void;
  boardName: string | null;
  setBoardName: (boardName: string | null) => void;
};

export const useBoardStore = create<BoardStoreState>(set => ({
  ownerDetails: null,
  setOwnerDetails: ownerDetails => set({ ownerDetails }),
  isPreviewMode: false,
  setIsPreviewMode: isPreviewMode => set({ isPreviewMode, notEditable: isPreviewMode }),
  previewVersion: null,
  screenViewBeforePreview: null,
  setScreenViewBeforePreview: screenViewBeforePreview => set({ screenViewBeforePreview }),
  setPreviewVersion: version => set({ previewVersion: version }),
  startPreview: (
    version: Version,
    screenViewBeforePreview: { x: number; y: number; zoom: number }
  ) =>
    set(state => ({
      isPreviewMode: true,
      previewVersion: version,
      notEditable: true,
      screenViewBeforePreview: state.screenViewBeforePreview ?? screenViewBeforePreview,
    })),
  endPreview: () =>
    set({
      isPreviewMode: false,
      previewVersion: null,
      notEditable: false,
      isRestoringVersion: false,
      screenViewBeforePreview: null,
    }),
  isRestoringVersion: false,
  setIsRestoringVersion: isRestoring =>
    set({ isRestoringVersion: isRestoring, notEditable: isRestoring }),
  notEditable: false,
  setNotEditable: notEditable => set({ notEditable }),
  versionUpdated: null,
  setVersionUpdated: versionUpdated => set({ versionUpdated }),
  isGlobalPasting: false,
  setGlobalPasting: (isPasting: boolean) => set({ isGlobalPasting: isPasting }),
  fullscreenNodeId: null,
  setFullscreenNodeId: (nodeId: string | null) => set({ fullscreenNodeId: nodeId }),
  showVideoFileLinkDialog: false,
  setShowVideoFileLinkDialog: (show: boolean) => set({ showVideoFileLinkDialog: show }),
  nodeDragMode: null,
  setNodeDragMode: mode => set({ nodeDragMode: mode }),
  boardName: null,
  setBoardName: boardName => set({ boardName }),
}));

/**
 * Hook to start previewing a version of the board
 * @param version The version to preview
 * @param getCurrentViewport Function to get the current viewport position and zoom
 * @param fitView Function to fit the view to the content
 * @returns Function to start the preview
 */
export const useStartPreview = () => {
  const startPreview = useBoardStore(state => state.startPreview);

  const startingPreview = (
    version: Version,
    getCurrentViewport: () => Viewport,
    fitView: FitView
  ) => {
    const currentViewport = getCurrentViewport();
    startPreview(version, currentViewport);
    fitView();
  };
  return startingPreview;
};

/**
 * Hook to end previewing a version of the board
 * @param setViewport Function to set the viewport position and zoom
 * @param fitView Function to fit the view to the content
 * @returns Function to end the preview
 */
export const useEndPreview = () => {
  const screenViewBeforePreview = useBoardStore(state => state.screenViewBeforePreview);
  const endPreview = useBoardStore(state => state.endPreview);

  const endingPreview = (setViewport: any, fitView: FitView) => {
    endPreview();
    if (screenViewBeforePreview) {
      setViewport(screenViewBeforePreview);
    } else {
      fitView();
    }
  };
  return endingPreview;
};
